import React, { useEffect, useState } from 'react';
import { Col, Container, Row, Carousel, ButtonGroup, NavDropdown, Dropdown, DropdownButton } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import AuthButton from '../../components/Buttons/AuthButton';
import IconButton from '../../components/Buttons/IconButton';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateField from '../../components/Fields/DateField';
import Form from '../../components/Fields/Form';
import Field from '../../components/Fields/Field';
import SelectListField from '../../components/Fields/SelectListField';
import TextareaField from '../../components/Fields/TextareaField';
import TextboxField from '../../components/Fields/TextboxField';
import { FieldConfig, FormConfig, SelectListOption } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import LicenseModel from '../../models/LicenseModel';
import DataService from '../../services/data.service';
import { DataResult, Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import { useTranslations } from '../../contexts/translationContext';
import IdParams from '../../types/IdParams';
import DateControl from '../../components/Fields/DateControl';
import Button from '../../components/Buttons/Button';
import FieldLabel from '../../components/Fields/FieldLabel';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import { BusinessPartnerTypeEnum } from '../../enums/BusinessPartnerTypeEnum';
import UserDefinedField from '../../components/Fields/UserDefinedField';
import SwitchField from '../../components/Fields/SwitchField';
import License_tab_Attachments from './Tabs/License_tab_Attachments';
import License_tab_Brands from './Tabs/License_tab_Brands';
import License_tab_ProductCategories from './Tabs/License_tab_ProductCategories';
import License_tab_Regions from './Tabs/License_tab_Regions';
import License_tab_BusinessPartners from './Tabs/License_tab_BusinessPartners';
import License_tab_Items from './Tabs/License_tab_Items';
import License_tab_RoyaltyRates from './Tabs/License_tab_RoyaltyRates';


const LicenseDetailsView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<LicenseModel>>({ isSuccesful: true, messages: [], data: undefined });
    const [licensors, setLicensors] = useState<SelectListOption[]>([]);
    const { handleResult, handleException } = useNotifications();
    const { translate } = useTranslations();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [formData, setFormData] = useState<LicenseModel>();
    const [licenseId, setLicenseId] = useState<number>()

    const divisions = useSelectListOptions('api/v1/selectlist/Divisions?activeOnly=true', '-- Select division');

    let idParams: IdParams = useParams();
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        if (licenseId !== parseInt(idParams.id)) {
            setLicenseId(parseInt(idParams.id));
        }
    }

    useEffect(() => {
        if (licenseId) {
            DataService.getById<LicenseModel>('api/v1/licenses/', licenseId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [licenseId, handleException]);

    useEffect(() => {
        if (formData) {
            translate('-- Select licensor').then(translated => {
                DataService.get<SelectListOption[]>('api/v1/selectlist/BusinessPartners?activeOnly=true&businessPartnerTypeId=' + BusinessPartnerTypeEnum.Licensor + '&currentValue=' + formData.licensorBusinessPartnerId + '&nullOption=' + translated)
                    .then(response => setLicensors(response))
                    .catch(e => handleException(e));
            });
        }
    }, [formData, handleException]);

    function submit() {
        if (formData) {
            DataService
                .update<LicenseModel>(`api/v1/licenses/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    let fields: FieldConfig[] = [
        { name: 'referenceCode', label: 'Referencecode', labelSize: 2 },
        { name: 'name', label: 'Name', labelSize: 2, validationRules: [{ type: 'isRequired' }] },
        { name: 'licensorBusinessPartnerId', label: 'Licensor', labelSize: 2 },
        { name: 'licenseeDivisionId', label: 'Licensee', labelSize: 2 },
        { name: 'beginDate', label: 'Active from', labelSize: 2, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'endDate', label: 'Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        { name: 'isValid', label: 'Valid', labelSize: 2 },
        { name: 'summary', label: 'Summary', labelSize: 2 },
        { name: 'sampleRemarks', label: 'Sample remarks', labelSize: 3 },
        { name: 'sellOffPeriod', label: 'Sell-off period (months)', labelSize: 3 },
    ];

    if (formData && formData.userDefinedFields) {
        formData.userDefinedFields.forEach((value, index) => {
            let label = value.userDefinedField.name
            fields.push({ name: 'userDefinedFields.' + index + '.value', label: label, labelSize: 3 });
        });
    }

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);
    const { isAuthorizedFor } = useAuth();

    if (licenseId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.CreateLicense)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <h5 style={{ textAlign: 'center', fontWeight: 'bold' }}>Loading...</h5>;

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem disabled width={220} icon="fa fa-magic" title="Generate a document" appFunction={ApplicationFunctions.CreateAttachment} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='License' actions={formActions} footer={(
                <Row>
                    <Col md='6'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateLicense}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                    <Col md='6' className='hidden-md'>
                        <ButtonBar offset={0}>
                        </ButtonBar>
                    </Col>
                </Row>
            )}
            >
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('referenceCode')} />
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('licensorBusinessPartnerId')} options={licensors} />
                            <SelectListField {...getFieldProps('licenseeDivisionId')} options={divisions} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                            <SwitchField {...getFieldProps('isValid')} />
                            <TextareaField {...getFieldProps('summary')} />
                        </Col>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('sampleRemarks')} />
                            <TextboxField {...getFieldProps('sellOffPeriod')} />
                            {formData && formData.userDefinedFields &&
                                formData.userDefinedFields.map((value, index) => {
                                    let name = 'userDefinedFields.' + index + '.value';
                                    return (<UserDefinedField {...getFieldProps(name)} userDefinedFieldValue={value} key={'userDefinedField' + index} />);
                                })
                            }
                        </Col>
                    </Row>
                </Container>
            </Form>
            {licenseId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey='tab_brands'
                                tabs={[
                                    {
                                        title: 'Brands',
                                        eventKey: 'tab_brands',
                                        component: <License_tab_Brands licenseId={licenseId} />
                                    },
                                    {
                                        title: 'Categories',
                                        eventKey: 'tab_productcategories',
                                        component: <License_tab_ProductCategories licenseId={licenseId} />
                                    },
                                    {
                                        title: 'Regions',
                                        eventKey: 'tab_regions',
                                        component: <License_tab_Regions licenseId={licenseId} />
                                    },
                                    {
                                        title: 'Business-partners',
                                        eventKey: 'tab_businesspartners',
                                        component: <License_tab_BusinessPartners licenseId={licenseId} />
                                    },
                                    {
                                        title: 'Items',
                                        eventKey: 'tab_items',
                                        component: <License_tab_Items licenseId={licenseId} />
                                    },
                                    {
                                        title: 'Attachments',
                                        eventKey: 'tab_attachments',
                                        component: <License_tab_Attachments licenseId={licenseId} />,
                                        applicationFunction: ApplicationFunctions.ViewAttachment
                                    },
                                    {
                                        title: 'Royalty rates',
                                        eventKey: 'tab_royaltyrates',
                                        component: <License_tab_RoyaltyRates licenseId={licenseId} />,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default LicenseDetailsView;